import { Modal, useModals } from 'almanac-ui/Modal'
import Styles from './ContentTypeSelectorWithCounts.module.scss'
import { ContentCategoryCounter } from './ContentCategoryCounter'
import { useMemo } from 'react'
import {
  ContentCategory,
  ContentTypeCounts,
  getAvailableContentCategories,
  getContentCategoryForScheduleContent,
} from 'utils/brandPlans/contentCategories'
import { Text } from 'blaze-ui/Text/Text'
import { useBrandPlanSchedule } from 'hooks/queries/schedules/useBrandPlanSchedule'
import { BrandPlanSchedule } from 'types'
import { Button } from 'blaze-ui/Button'

interface ContentTypeSelectorWithCountsProps {
  scheduleId: number
  onNext: (schedule?: BrandPlanSchedule) => void
  counts: ContentTypeCounts
  setCounts: React.Dispatch<React.SetStateAction<ContentTypeCounts>>
  isStrictBrandControl?: boolean
}

export const ContentTypeSelectorWithCounts = ({
  onNext,
  scheduleId,
  counts,
  setCounts,
  isStrictBrandControl = false,
}: ContentTypeSelectorWithCountsProps) => {
  const { closeModal } = useModals()
  const { data: schedule } = useBrandPlanSchedule(scheduleId)
  const categories = getAvailableContentCategories(isStrictBrandControl)

  const totalNewPosts = useMemo(
    () =>
      categories.reduce((sum, category) => {
        return sum + counts[category]
      }, 0),
    [categories, counts],
  )

  const hasSelection = Object.values(counts).some((count) => count > 0)

  const existingCountByCategory = useMemo(() => {
    const tallies = Object.fromEntries(categories.map((c) => [c, 0])) as Record<ContentCategory, number>
    for (const item of schedule?.contents ?? []) {
      const cat = getContentCategoryForScheduleContent(item)
      if (cat && cat in tallies) tallies[cat] += 1
    }
    return tallies
  }, [categories, schedule?.contents])

  return (
    <>
      <Modal.Header title="Choose how many new posts you would like" />
      <Modal.Content>
        <div className={Styles.body}>
          <div className={Styles.grid}>
            {categories.map((category) => (
              <ContentCategoryCounter
                key={category}
                category={category}
                count={counts[category]}
                currentCount={existingCountByCategory[category]}
                onCountChange={(category, count) =>
                  setCounts((prev) => ({
                    ...prev,
                    [category]: Math.max(0, count),
                  }))
                }
              />
            ))}
          </div>
        </div>
      </Modal.Content>
      <Modal.Footer>
        <Modal.FooterContent slot="left">
          <Modal.FooterButton size="lg" variant="tertiary" onPress={closeModal}>
            Cancel
          </Modal.FooterButton>
        </Modal.FooterContent>
        <Modal.FooterContent slot="right">
          <div className={Styles.footerRight}>
            {hasSelection && (
              <Text variant="label" color="var(--dark-40)">
                {totalNewPosts} new posts
              </Text>
            )}
            <Button onPress={() => onNext(schedule)} isDisabled={!hasSelection} size="lg">
              Continue
            </Button>
          </div>
        </Modal.FooterContent>
      </Modal.Footer>
    </>
  )
}
